/**
 * Setting a new password from a reset link.
 *
 * Every other session is destroyed on success, server-side. Whoever prompted the reset may be
 * sitting in one right now, and leaving them signed in would make the reset theatre.
 */

import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { Alert, Button, Card, Field } from "@/components/ui";
import { PasswordInput } from "@/components/password-input";
import { AuthShell } from "./AuthShell";
import { usePageTitle } from "@/lib/title";

export function ResetPasswordPage() {
  usePageTitle("Set a new password");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";
  const [mismatch, setMismatch] = useState(false);

  const reset = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.postAsHuman("/auth/reset-password", input, "reset_password"),
    onSuccess: () => navigate("/sign-in?reset=1"),
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password") ?? "");
    // Checked here rather than server-side: the confirmation box exists to catch a typo, and the
    // server has no business knowing it was typed twice.
    if (password !== String(form.get("confirm") ?? "")) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    reset.mutate({ token, password });
  };

  if (!token) {
    return (
      <AuthShell>
        <Card>
          <h1 className="text-xl font-semibold">That link is incomplete</h1>
          <p className="mt-1 text-sm text-ink-500">
            Open the link from the email exactly as it was sent, or <Link to="/forgot-password" className="link">ask for a new one</Link>.
          </p>
        </Card>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Card>
        <h1 className="text-xl font-semibold">Set a new password</h1>
        <p className="mt-1 text-sm text-ink-500">You will be signed out everywhere else.</p>

        <form onSubmit={submit} className="mt-5 space-y-4">
          {reset.errorMessage ? <Alert tone="danger">{reset.errorMessage}</Alert> : null}
          {mismatch ? <Alert tone="danger">The two passwords are not the same.</Alert> : null}

          <Field label="New password" htmlFor="password" required hint="At least 8 characters.">
            <PasswordInput id="password" autoComplete="new-password" required minLength={8} />
          </Field>

          <Field label="Type it again" htmlFor="confirm" required>
            <PasswordInput id="confirm" name="confirm" autoComplete="new-password" required minLength={8} />
          </Field>

          <Button type="submit" disabled={reset.isPending} className="w-full">
            {reset.isPending ? "Saving…" : "Set the password"}
          </Button>
        </form>
      </Card>
    </AuthShell>
  );
}
